/**
 * Your ½–5 rating for something you've watched.
 *
 * Stored as `rating10` on Letterboxd's 1–10 scale rather than 1–5, because
 * that is the field the import and export already round-trip: a rating set
 * here exports to Letterboxd, and one imported from Letterboxd lands here.
 * The whole 1–10 range is reachable, so every rating Letterboxd can express
 * survives a round trip in both directions.
 *
 * Each star cycles: first click is the half, second the full, third clears it.
 * That fits half-stars into five targets without hit-testing which side of a
 * glyph a click landed on — which is unreliable at this size and impossible
 * from a keyboard.
 *
 * Only shown once an entry is watched. Rating something you haven't seen is a
 * different feature (an anticipation score), and conflating them would make
 * both meaningless.
 */

import type { EntryId } from "@schema/catalog.ts";
import { useProgressStore } from "@/state/progress.ts";
import styles from "./StarRating.module.css";

const STARS = [1, 2, 3, 4, 5];

/** Pretty-print half-steps: 4 not "4.0", 3.5 not "3.50". */
const fmt = (stars: number) => (Number.isInteger(stars) ? String(stars) : stars.toFixed(1));

/**
 * The next value for clicking `star`, given what's set now.
 *
 * half → full → cleared, and any other state jumps straight to this star's
 * half. So a first click anywhere is always the half, which makes the cycle
 * learnable without having to remember where you were.
 */
export function nextRating(current: number | null, star: number): number | null {
  const full = star * 2;
  const half = full - 1;
  if (current === half) return full;
  if (current === full) return null;
  return half;
}

function actionLabel(current: number | null, star: number): string {
  const next = nextRating(current, star);
  return next === null ? "Clear your rating" : `Rate ${fmt(next / 2)} out of 5`;
}

export function StarRating({ entryId, title }: { entryId: EntryId; title: string }) {
  const rating10 = useProgressStore((s) => s.records[entryId]?.rating10 ?? null);
  const setRating = useProgressStore((s) => s.setRating);

  const stars = rating10 === null ? 0 : rating10 / 2;

  return (
    <fieldset className={styles.rating} aria-label={`Your rating for ${title}`}>
      {STARS.map((star) => {
        const filled = stars >= star;
        const half = !filled && stars >= star - 0.5;
        return (
          <button
            key={star}
            type="button"
            className={`${styles.star} ${filled ? styles.filled : ""} ${half ? styles.half : ""}`}
            onClick={() => setRating(entryId, nextRating(rating10, star))}
            // Labelled by what the click will do, not by the star's position.
            // "4 stars" on a control that is about to set 3.5 is a lie, and
            // the position is already conveyed by reading order.
            aria-label={actionLabel(rating10, star)}
            aria-pressed={filled || half}
          >
            <span aria-hidden="true">★</span>
          </button>
        );
      })}
      <span className={styles.srOnly} aria-live="polite">
        {rating10 === null ? "Not rated" : `Rated ${fmt(stars)} out of 5`}
      </span>
    </fieldset>
  );
}
